import { yupResolver } from "@hookform/resolvers/yup";
import { useHeaderHeight } from "expo-router/react-navigation";
import { useRouter } from "expo-router";
import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import * as yup from "yup";

import OrganizationFormView, { IForm } from "./OrganizationFormView";

import { spacingForDevicesWithoutNotch } from "@/config/constants";
import { AnalyticsTracker } from "@/features/analytics";
import { useCurrentUser } from "@/features/auth";
import {
  CurrencyCode,
  InvitationsListMyOrganizationsDocument,
  InvitationsListMyOrganizationsQuery,
  InvitationsListOrganizationsDocument,
  InvitationsListOrganizationsQuery,
  InvitationsOrganizationFragment,
  useInvitationsCreateOrganizationMutation,
  useInvitationsListSportsQuery,
  useInvitationsUpdateOrganizationMutation,
} from "@/generated/graphql";
import useMobileScreen from "@/hooks/useMobileScreen";
import useRecordUserGoal, { GoalType } from "@/hooks/useRecordUserGoal";

const validCurrencyCodes = Object.values(CurrencyCode);

const refetchQueries = ["AuthGetUser"];

const schema = yup.object().shape({
  name: yup.string().required("Can't be blank"),
  currencyCode: yup
    .string()
    .oneOf(validCurrencyCodes)
    .required("Can't be blank"),
  sportId: yup.string().required("Can't be blank"),
  intakeFormUrl: yup.string().url().nullable().optional(),
});

/**
 * Props for the OrganizationFormContainer component.
 */
interface Props {
  organization?: InvitationsOrganizationFragment;
}

/**
 * Container component for creating or updating an organization with form validation and state management.
 * @param root0 - The component props
 * @param root0.organization - Optional organization data fragment for editing mode; if undefined, creates new organization
 */
const OrganizationFormContainer = ({ organization }: Props) => {
  const currentUser = useCurrentUser();
  const mutationType = organization ? "update" : "create";
  const heightHeader = useHeaderHeight();
  const { top, bottom } = useSafeAreaInsets();
  const isSmallScreen = useMobileScreen();
  const recordUserGoal = useRecordUserGoal({
    goalType:
      mutationType === "update"
        ? GoalType.UpdateOrganization
        : GoalType.CreateOrganization,
    value: `${mutationType} Organization`,
  });
  const [createOrganization] = useInvitationsCreateOrganizationMutation({
    refetchQueries,
  });
  const [updateOrganization] = useInvitationsUpdateOrganizationMutation({
    refetchQueries,
  });
  const { data: sportsData } = useInvitationsListSportsQuery();
  const sports = useMemo(
    () => sportsData?.listSports.edges.map(e => e.node) || [],
    [sportsData]
  );
  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = useForm<IForm>({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues: {
      name: organization?.name ?? "",
      currencyCode: organization?.currencyCode ?? CurrencyCode.Usd,
      sportId: organization?.sport?.id ?? "",
      intakeFormUrl: organization?.intakeFormUrl ?? "",
    },
  });

  const keyboardVerticalOffset =
    heightHeader +
    (top || spacingForDevicesWithoutNotch) +
    (bottom + spacingForDevicesWithoutNotch);

  const router = useRouter();

  const submit = useCallback(
    ({ name, currencyCode, sportId }: IForm) => {
      return (
        organization
          ? updateOrganization({
              onCompleted: () => {
                recordUserGoal({
                  value: name,
                  goalType: GoalType.UpdateOrganization,
                });
                // SE-4010: successful org edit.
                void AnalyticsTracker.user.logOrganizationUpdated({
                  organization_id: organization.id,
                });
              },
              variables: {
                input: {
                  id: organization.id,
                  name,
                  currencyCode,
                  sportId,
                },
              },
            })
          : createOrganization({
              update: (cache, { data, errors }) => {
                const existingData = cache.readQuery({
                  query: currentUser?.isAdmin
                    ? InvitationsListOrganizationsDocument
                    : InvitationsListMyOrganizationsDocument,
                });
                const temporaryData = data?.createOrganization;
                if (!!existingData && !!temporaryData && !errors) {
                  cache.writeQuery({
                    query: currentUser?.isAdmin
                      ? InvitationsListOrganizationsDocument
                      : InvitationsListMyOrganizationsDocument,
                    data: currentUser?.isAdmin
                      ? {
                          listOrganizations: {
                            ...(
                              existingData as InvitationsListOrganizationsQuery
                            ).listOrganizations,
                            edges: [
                              ...(
                                existingData as InvitationsListOrganizationsQuery
                              ).listOrganizations.edges,
                              {
                                __typename: "OrganizationEdge",
                                cursor: `temp-cursor-${temporaryData.id}`,
                                node: temporaryData,
                              },
                            ],
                          },
                        }
                      : {
                          listMyOrganizations: {
                            ...(
                              existingData as InvitationsListMyOrganizationsQuery
                            ).listMyOrganizations,
                            edges: [
                              ...(
                                existingData as InvitationsListMyOrganizationsQuery
                              ).listMyOrganizations.edges,
                              {
                                __typename: "OrganizationEdge",
                                cursor: `temp-cursor-${temporaryData.id}`,
                                node: temporaryData,
                              },
                            ],
                          },
                        },
                  });
                }
              },
              optimisticResponse: {
                __typename: "Mutation",
                createOrganization: {
                  __typename: "Organization",
                  id: `temp-id-${new Date().toISOString()}`,
                  name,
                  currencyCode,
                  sport: {
                    id: sportId,
                    name:
                      sports.find(sport => sport.id === sportId)?.name ?? "",
                  },
                  intakeFormUrl: null,
                },
              },
              onCompleted: completed => {
                recordUserGoal({
                  value: name,
                  goalType: GoalType.CreateOrganization,
                });
                // SE-4010: successful org creation — onCompleted receives the
                // server response (not the optimistic one), so the id is real.
                const createdOrgId = completed.createOrganization?.id;
                if (createdOrgId) {
                  void AnalyticsTracker.user.logOrganizationCreated({
                    organization_id: createdOrgId,
                  });
                }
              },
              variables: {
                input: { name, currencyCode, sportId },
              },
            })
      )
        .then(() => router.navigate("/organizations"))
        .catch(console.log);
    },
    [
      createOrganization,
      currentUser?.isAdmin,
      organization,
      recordUserGoal,
      router,
      sports,
      updateOrganization,
    ]
  );

  const doSubmit = handleSubmit(submit);

  return (
    <OrganizationFormView
      onSubmit={doSubmit}
      isValid={isValid}
      control={control}
      errors={errors}
      loading={isSubmitting}
      sports={sports}
      isSmallScreen={isSmallScreen}
      keyboardVerticalOffset={keyboardVerticalOffset}
    />
  );
};

export default OrganizationFormContainer;
